import { z } from 'zod'
import type { StoredJson } from '../helpers/zod-error.js'
import type { LoanFactCode } from './fact-codes.js'
import {
  HAPPY_PATH_FROM_CLEAR_TO_CLOSE,
  HAPPY_PATH_FROM_PRE_APPROVED,
  type UnderwritingGate,
  type UnderwritingSeverity,
  tryStatusGate,
} from './gates.js'
import type { UnderwritingOperator } from './operators.js'
import type { UnderwritingRuleCode } from './rule-codes.js'

const RESERVES_MONTHS_MESSAGE = 'Enter whole months between 1 and 120'
const LOAN_AMOUNT_MESSAGE = 'Enter a positive dollar amount'

/**
 * Stored and form input. Coerces numeric strings ("2", "180000") then checks
 * the range. `.strict()` rejects a payload that mixes both shapes.
 */
export const minReservesMonthsThresholdSchema = z
  .object({
    months: z.coerce
      .number()
      .int(RESERVES_MONTHS_MESSAGE)
      .min(1, RESERVES_MONTHS_MESSAGE)
      .max(120, RESERVES_MONTHS_MESSAGE),
  })
  .strict()

export const minLoanAmountThresholdSchema = z
  .object({
    amount: z.coerce
      .number()
      .finite(LOAN_AMOUNT_MESSAGE)
      .positive(LOAN_AMOUNT_MESSAGE),
  })
  .strict()

/**
 * Wire shape for responses. Numbers only — a string here means the producer
 * skipped parsing. Same bounds as the input schemas.
 */
export const minReservesMonthsWireSchema = z
  .object({
    months: z
      .number()
      .int()
      .min(1)
      .max(120),
  })
  .strict()

export const minLoanAmountWireSchema = z
  .object({
    amount: z.number().finite().positive(),
  })
  .strict()

export type MinReservesMonthsThreshold = z.infer<
  typeof minReservesMonthsWireSchema
>
export type MinLoanAmountThreshold = z.infer<
  typeof minLoanAmountWireSchema
>

export type RuleThreshold =
  | MinReservesMonthsThreshold
  | MinLoanAmountThreshold

/**
 * Rule code and threshold kept as one value so a reserves rule cannot carry
 * an amount threshold, and a min-loan rule cannot carry months.
 */
export type StoredUnderwritingThreshold =
  | { ruleCode: 'MIN_RESERVES_MONTHS'; threshold: MinReservesMonthsThreshold }
  | { ruleCode: 'MIN_LOAN_AMOUNT'; threshold: MinLoanAmountThreshold }

/** Validate stored or form JSON with the input schema (numeric strings coerce). */
export function parseStoredUnderwritingThreshold(
  ruleCode: UnderwritingRuleCode,
  raw: StoredJson,
): StoredUnderwritingThreshold {
  switch (ruleCode) {
    case 'MIN_RESERVES_MONTHS':
      return {
        ruleCode,
        threshold: minReservesMonthsThresholdSchema.parse(raw),
      }
    case 'MIN_LOAN_AMOUNT':
      return {
        ruleCode,
        threshold: minLoanAmountThresholdSchema.parse(raw),
      }
    default: {
      const unexpected: never = ruleCode
      throw new Error(`Unknown underwriting rule code: ${unexpected}`)
    }
  }
}

/**
 * Validate JSON that is about to leave the API. Numbers only — a numeric
 * string means the row was stored without parsing.
 */
export function parseWireUnderwritingThreshold(
  ruleCode: UnderwritingRuleCode,
  raw: StoredJson,
): StoredUnderwritingThreshold {
  switch (ruleCode) {
    case 'MIN_RESERVES_MONTHS':
      return {
        ruleCode,
        threshold: minReservesMonthsWireSchema.parse(raw),
      }
    case 'MIN_LOAN_AMOUNT':
      return {
        ruleCode,
        threshold: minLoanAmountWireSchema.parse(raw),
      }
    default: {
      const unexpected: never = ruleCode
      throw new Error(`Unknown underwriting rule code: ${unexpected}`)
    }
  }
}

export type { StoredJson }

export type UnderwritingRuleDef = {
  code: UnderwritingRuleCode
  label: string
  description: string
  factCode: LoanFactCode
  operator: UnderwritingOperator
  /** Gates where a failing or unavailable check is BLOCK (else WARN when shown). */
  blockOnGates: readonly UnderwritingGate[]
  /** Validate stored JSON into this rule's threshold. */
  parseThreshold: (raw: StoredJson) => RuleThreshold
  /** Numeric threshold used in compare (single number for Phase 1). */
  thresholdValue: (threshold: RuleThreshold) => number
  formatRequired: (threshold: RuleThreshold) => string
  formatActual: (value: number) => string
  messageFail: (args: {
    actualLabel: string
    requiredLabel: string
  }) => string
  messageUnavailable: (args: { requiredLabel: string }) => string
}

function moneyLabel(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function requireMonths(threshold: RuleThreshold): MinReservesMonthsThreshold {
  return minReservesMonthsThresholdSchema.parse(threshold)
}

function requireAmount(threshold: RuleThreshold): MinLoanAmountThreshold {
  return minLoanAmountThresholdSchema.parse(threshold)
}

const minLoanBlockGates: UnderwritingGate[] = HAPPY_PATH_FROM_PRE_APPROVED.flatMap(
  (s) => {
    const g = tryStatusGate(s)
    return g ? [g] : []
  },
)

const minReservesBlockGates: UnderwritingGate[] =
  HAPPY_PATH_FROM_CLEAR_TO_CLOSE.flatMap((s) => {
    const g = tryStatusGate(s)
    return g ? [g] : []
  })

/** Exhaustive catalog — missing a rule code is a compile error. */
export const UNDERWRITING_RULE_CATALOG: {
  [K in UnderwritingRuleCode]: UnderwritingRuleDef
} = {
  MIN_RESERVES_MONTHS: {
    code: 'MIN_RESERVES_MONTHS',
    label: 'Minimum months of reserves',
    description:
      'Borrower must have at least this many months of reserves before clear to close.',
    factCode: 'reserves_months',
    operator: 'gte',
    blockOnGates: minReservesBlockGates,
    parseThreshold(raw) {
      return minReservesMonthsThresholdSchema.parse(raw)
    },
    thresholdValue(threshold) {
      return requireMonths(threshold).months
    },
    formatRequired(threshold) {
      const m = requireMonths(threshold).months
      return m === 1 ? '1 month' : `${m} months`
    },
    formatActual(value) {
      return value === 1 ? '1 month' : `${value} months`
    },
    messageFail({ actualLabel, requiredLabel }) {
      return `Reserves are ${actualLabel}; this lender requires at least ${requiredLabel}.`
    },
    messageUnavailable({ requiredLabel }) {
      return `Reserves can't be verified yet; this lender requires at least ${requiredLabel} before clear to close.`
    },
  },
  MIN_LOAN_AMOUNT: {
    code: 'MIN_LOAN_AMOUNT',
    label: 'Minimum loan amount',
    description:
      'Pre-approval max loan amount must be at least this dollar amount.',
    factCode: 'max_loan_amount',
    operator: 'gte',
    blockOnGates: minLoanBlockGates,
    parseThreshold(raw) {
      return minLoanAmountThresholdSchema.parse(raw)
    },
    thresholdValue(threshold) {
      return requireAmount(threshold).amount
    },
    formatRequired(threshold) {
      return moneyLabel(requireAmount(threshold).amount)
    },
    formatActual(value) {
      return moneyLabel(value)
    },
    messageFail({ actualLabel, requiredLabel }) {
      return `Max loan amount is ${actualLabel}; this lender requires at least ${requiredLabel}.`
    },
    messageUnavailable({ requiredLabel }) {
      return `Max loan amount isn't set yet; this lender requires at least ${requiredLabel} before pre-approval.`
    },
  },
}

export function severityForGate(
  def: UnderwritingRuleDef,
  gate: UnderwritingGate,
): UnderwritingSeverity {
  return def.blockOnGates.includes(gate) ? 'BLOCK' : 'WARN'
}
